// THE BRIDGE BETWEEN A TYPED REJECTION AND THE WORDS A PERSON READS
// (bead hf-e6-commitments-coverage-udt.4.2).
//
// The claim guard returns a `CommitmentRejection` — a structural union carrying
// numbers and entity names. `@herefor/product-language` holds sentences, keyed by
// the plan's own rejection codes. Neither package may import the other: contracts
// must stay backend-agnostic and copy-free (plan §36.4), and product-language must
// not depend on a schema. So the mapping lives here, in the app, where both are
// already dependencies.
//
// IT IS TOTAL ON PURPOSE. Every rejection the guard can produce has a sentence, and
// the compiler will not let a new one slip through as an unhandled case — because
// the alternative is a person seeing a blank panel, or worse, a raw code, at the
// exact moment they were trying to be kind.

import type { CommitmentRejection } from "@herefor/contracts";
import {
  ERROR_MESSAGES,
  REJECTION_MESSAGES,
  type ActionableMessage,
} from "@herefor/product-language";

/**
 * The message for any rejection at all.
 *
 * The fallbacks are chosen so the sentence is TRUE rather than generic. A missing
 * capability is `permissionDenied`, not "something went wrong" — telling somebody
 * their tap failed when in fact they were never allowed to do it sends them back to
 * try again, which is worse than a plain answer.
 */
export function messageFor(rejection: CommitmentRejection): ActionableMessage {
  switch (rejection.kind) {
    case "capacity_full":
      return REJECTION_MESSAGES.NEED_JUST_COVERED;
    // NOT the same sentence as `capacity_full`, and the difference is the point: the
    // need is not covered, so "someone else just stepped in" would contradict the
    // Please recheck the person is looking at (bead hf-83p).
    case "places_awaiting_recheck":
      return REJECTION_MESSAGES.PLACES_AWAITING_RECHECK;
    case "revision_stale":
    case "needs_recheck":
      return REJECTION_MESSAGES.STALE_REVISION;
    case "already_committed":
      return REJECTION_MESSAGES.ALREADY_COMMITTED;
    case "need_not_open":
      return REJECTION_MESSAGES.NEED_NOT_ACTIVE;
    // `not_responsible` is somebody else's commitment (COM-010). "You can't make this
    // change" is the truthful sentence: it is not theirs to withdraw, and saying so
    // beats a blank refusal.
    case "missing_capability":
    case "not_responsible":
      return ERROR_MESSAGES.permissionDenied;
    case "not_a_member":
      return ERROR_MESSAGES.accessRemoved;
    case "circle_not_found":
    case "not_found":
      return ERROR_MESSAGES.itemUnavailable;
    // A window rejection cannot reach a claim — a claim proposes no window, it takes
    // the need's — but the contract's union carries them, so they are answered rather
    // than left to a default that would swallow a genuine bug.
    case "empty_window":
    case "crosses_day_boundary":
    case "validation_failed":
    case "conflicts_with_commitment":
      return ERROR_MESSAGES.serviceUnavailable;
  }
}

/**
 * Is this the moment this bead exists for?
 *
 * Kept as a named predicate rather than an inline comparison because two places have
 * to agree about it — the component chooses a warm tone, and the controller refreshes
 * the plan — and a race loss handled warmly in one and coldly in the other would be
 * worse than handling it coldly in both.
 */
export function isRaceLoss(
  rejection: CommitmentRejection,
): rejection is Extract<CommitmentRejection, { kind: "capacity_full" }> {
  return rejection.kind === "capacity_full";
}
